import { useParams } from 'react-router-dom'

import { api } from '../api/client'
import type { Alert, IncidentDetail, Page } from '../api/types'
import { Card, Empty, ErrorNote, Labels, SeverityBadge, Spinner, StatusBadge, TableScroll, formatDateTime, humanDuration, since } from '../components/ui'
import { useAction, useQuery } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'

export function IncidentDetailPage() {
  const { id = '' } = useParams()
  const { can } = useAuth()

  const incident = useQuery(() => api.get<IncidentDetail>(`/incidents/${id}`), [id])
  const alerts = useQuery(
    () => api.get<Page<Alert>>(`/incidents/${id}/alerts`, { limit: 50 }),
    [id],
  )
  const action = useAction()

  const act = async (what: 'ack' | 'resolve' | 'silence' | 'unsilence') => {
    const body =
      what === 'silence' ? { duration_minutes: 60, message: '' } : what === 'unsilence' ? undefined : { message: '' }
    if (await action.run(() => api.post(`/incidents/${id}/${what}`, body))) incident.reload()
  }

  if (incident.loading && !incident.data) return <Spinner />
  if (!incident.data) return <ErrorNote>{incident.error ?? 'Инцидент не найден'}</ErrorNote>

  const data = incident.data

  return (
    <>
      <ErrorNote>{action.error}</ErrorNote>

      <Card
        title={
          <span className="flex items-center gap-3">
            <SeverityBadge severity={data.severity} />
            <span className="text-base">{data.title}</span>
            <StatusBadge status={data.status} />
          </span>
        }
        actions={
          <>
            {can('alerts.ack') && data.status !== 'acknowledged' && data.status !== 'resolved' && (
              <button className="btn-primary" disabled={action.pending} onClick={() => act('ack')}>
                Принять
              </button>
            )}
            {can('alerts.silence') &&
              (data.status === 'silenced' ? (
                <button className="btn-ghost" onClick={() => act('unsilence')}>
                  Снять silence
                </button>
              ) : (
                data.status !== 'resolved' && (
                  <button className="btn-ghost" onClick={() => act('silence')}>
                    Заглушить на час
                  </button>
                )
              ))}
            {can('alerts.resolve') && data.status !== 'resolved' && (
              <button className="btn-danger" onClick={() => act('resolve')}>
                Закрыть
              </button>
            )}
          </>
        }
      >
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 p-4 text-sm">
          <Field label="Начался">{formatDateTime(data.started_at)}</Field>
          <Field label="Возраст">{since(data.started_at)}</Field>
          <Field label="Последний алерт">{formatDateTime(data.last_seen_at)}</Field>
          <Field label="Алертов в группе">{data.alerts_count}</Field>
          <Field label="Время до ack">{humanDuration(data.time_to_ack_seconds)}</Field>
          <Field label="Время до закрытия">{humanDuration(data.time_to_resolve_seconds)}</Field>
          <Field label="Уровень эскалации">{data.escalation_level}</Field>
          <Field label="Silence до">{formatDateTime(data.silenced_until)}</Field>
        </dl>

        <div className="px-4 pb-4 space-y-3">
          <div>
            <div className="label">Метки</div>
            <Labels labels={data.labels} limit={50} />
          </div>
          {Object.keys(data.annotations ?? {}).length > 0 && (
            <div>
              <div className="label">Аннотации</div>
              <div className="space-y-1 text-sm text-slate-300">
                {Object.entries(data.annotations).map(([key, value]) => (
                  <div key={key}>
                    <span className="text-slate-500">{key}: </span>
                    {value}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Таймлайн">
          {data.events.length === 0 ? (
            <Empty>Событий пока нет.</Empty>
          ) : (
            <ol className="p-4 space-y-3">
              {data.events.map((event) => (
                <li key={event.id} className="flex gap-3 text-sm">
                  <span className="text-xs text-slate-500 tabular-nums w-36 shrink-0">
                    {formatDateTime(event.created_at)}
                  </span>
                  <span>
                    <span className="text-slate-200">{event.kind}</span>
                    {event.from_status && event.to_status && (
                      <span className="text-slate-500">
                        {' '}
                        {event.from_status} → {event.to_status}
                      </span>
                    )}
                    {event.message && (
                      <div className="text-slate-400 text-xs mt-0.5">{event.message}</div>
                    )}
                    <div className="text-[11px] text-slate-600 mt-0.5">{event.actor_kind}</div>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Card>

        <Card title={`Алерты — ${alerts.data?.total ?? 0}`}>
          {alerts.loading && !alerts.data ? (
            <Spinner />
          ) : (alerts.data?.items.length ?? 0) === 0 ? (
            <Empty>Нет алертов.</Empty>
          ) : (
            <TableScroll>
              <thead>
                <tr>
                  <th className="th">Источник</th>
                  <th className="th">Состояние</th>
                  <th className="th">Заголовок</th>
                  <th className="th">Принят</th>
                </tr>
              </thead>
              <tbody>
                {alerts.data!.items.map((alert) => (
                  <tr key={alert.id}>
                    <td className="td text-slate-400">{alert.source_type}</td>
                    <td className="td">
                      <span
                        className={
                          alert.state === 'resolved' ? 'text-emerald-400' : 'text-severity-critical'
                        }
                      >
                        {alert.state}
                      </span>
                    </td>
                    <td className="td">{alert.title}</td>
                    <td className="td text-slate-400 tabular-nums">
                      {formatDateTime(alert.received_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableScroll>
          )}
        </Card>
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="label">{label}</dt>
      <dd className="text-slate-200">{children}</dd>
    </div>
  )
}
